import type { PropsWithChildren, ReactNode } from 'react';
import { cn } from '@/shared/utils';
import { ChevronRightIcon } from 'lucide-react';
import { tv } from 'tailwind-variants';

type Props = {
  icon?: ReactNode;
  title: ReactNode | string;
  subtitle?: ReactNode | string;
  onClick?: () => void;
  showChevron?: boolean;
  className?: string;
  iconWrapperClassName?: string;
  subtitleVariant?: 'default' | 'warning';
};

const userProfileActionClasses = tv({
  slots: {
    container:
      'group px-[2.4rem] flex items-center justify-between min-h-[5.6rem] w-full gap-[1.6rem] rounded-[1.2rem] hover:bg-secondary-bg cursor-pointer',
    content: 'flex items-center gap-[1.6rem]',
    textWrapper: 'flex flex-col gap-[0.2rem]',
    iconWrapper: 'size-[2.4rem] flex items-center justify-center',
    title: 'headline4 text-primary text-left',
    subtitle: 'text-secondary text-left',
    chevronWrapper: 'size-10 flex items-center justify-center',
    chevron: 'size-10 text-tertiary group-hover:text-primary',
  },
  variants: {
    showChevron: {
      true: {
        container: 'cursor-pointer',
      },
    },
  },
});

export function UserProfileAction({
  icon,
  title,
  subtitle,
  onClick,
  showChevron = true,
  children,
  className,
  iconWrapperClassName,
}: PropsWithChildren<Props>) {
  const {
    container,
    content,
    textWrapper,
    iconWrapper,
    title: titleClass,
    subtitle: subtitleClass,
    chevronWrapper,
    chevron: chevronClass,
  } = userProfileActionClasses({
    showChevron: showChevron && !children,
  });

  const subtitleComponent =
    typeof subtitle === 'string' ? (
      <span className={subtitleClass()}>{subtitle}</span>
    ) : subtitle ? (
      subtitle
    ) : null;

  return (
    <div className={cn(container(), className)} onClick={onClick}>
      <div className={content()}>
        {icon && (
          <div className={cn(iconWrapper(), iconWrapperClassName)}>{icon}</div>
        )}
        <div className={textWrapper()}>
          <p className={titleClass()}>{title}</p>
          {subtitleComponent}
        </div>
      </div>
      {showChevron && !children && (
        <div className={chevronWrapper()}>
          <ChevronRightIcon className={chevronClass()} />
        </div>
      )}
      {children}
    </div>
  );
}
